import type { LinkComponentProps } from "@tanstack/react-router";
import type {
  BreadcrumbProps as RACBreadcrumbProps,
  BreadcrumbsProps as RACBreadcrumbsProps,
  LinkProps as RACLinkProps,
} from "react-aria-components";

import { createLink } from "@tanstack/react-router";
import { forwardRef } from "react";
import { useLink } from "react-aria";
import { useObjectRef } from "react-aria";
import { useFocusRing } from "react-aria";
import { useHover } from "react-aria";
import { mergeProps } from "react-aria";
import {
  composeRenderProps,
  Breadcrumb as RACBreadcrumb,
  Breadcrumbs as RACBreadcrumbs,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

import { Tooltip, TooltipTrigger } from "@/components/tooltip";
import { useIsMobileScreen } from "@/hooks/useIsMobileScreen";

import { ChevronRightIcon } from "../icons";

export function Breadcrumbs<T extends object>({
  className,
  ...props
}: RACBreadcrumbsProps<T>) {
  return (
    <RACBreadcrumbs {...props} className={twMerge("flex gap-1", className)} />
  );
}

type BreadcrumbProps = { onLinkClick?: VoidFunction } & LinkComponentProps &
  RACBreadcrumbProps &
  RACLinkProps;

export function Breadcrumb(props: BreadcrumbProps) {
  // Separate props for TanStack Router from React Aria props
  const {
    children,
    hash,
    onLinkClick,
    params,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref: _ref,
    search,
    state,
    to,
    ...otherProps
  } = props as { ref?: unknown } & BreadcrumbProps;

  const isMobile = useIsMobileScreen();
  const showTooltip = !isMobile && typeof children === "string";

  return (
    <RACBreadcrumb
      {...otherProps}
      className={composeRenderProps(
        otherProps.className as RACBreadcrumbProps["className"],
        (className) => twMerge("flex items-center gap-1", className),
      )}
    >
      {props.to && isMobile && (
        <div className="flex gap-2 items-center">
          <CreatedLinkComponent
            className={twMerge(
              "gap-md rounded-sm px-md py-xs text-sm font-medium text-breadcrumb-fg outline-none",
              "hover:bg-breadcrumb-bg-hover",
              !to && "font-semibold bg-breadcrumb-bg-hover",
              "max-w-[200px] truncate",
            )}
            hash={hash}
            onPress={onLinkClick}
            params={params}
            search={search}
            state={state}
            to={to}
          >
            ...
          </CreatedLinkComponent>
          <ChevronRightIcon className="size-4 text-fg-senary" />
        </div>
      )}
      <TooltipTrigger>
        <CreatedLinkComponent
          className={twMerge(
            "gap-md rounded-sm px-md py-xs text-sm font-medium text-breadcrumb-fg outline-none",
            "hover:bg-breadcrumb-bg-hover",
            !to && "font-semibold bg-breadcrumb-bg-hover",
            "max-w-[200px] truncate",
          )}
          disabled={isMobile}
          hash={hash}
          onPress={onLinkClick}
          params={params}
          search={search}
          state={state}
          to={to}
        >
          {children}
        </CreatedLinkComponent>

        {showTooltip && <Tooltip placement="bottom">{children}</Tooltip>}
      </TooltipTrigger>

      {props.to && !isMobile && (
        <ChevronRightIcon className="size-4 text-fg-senary" />
      )}
    </RACBreadcrumb>
  );
}

const RACLinkComponent = forwardRef<HTMLAnchorElement, RACLinkProps>(
  (props, forwardedRef) => {
    const ref = useObjectRef(forwardedRef);

    const { isPressed, linkProps } = useLink(props, ref);
    const { hoverProps, isHovered } = useHover(props);
    const { focusProps, isFocused, isFocusVisible } = useFocusRing(props);

    // Filter out non-HTML props to avoid the type error
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { slot: _, ...filteredProps } = props as {
      slot?: unknown;
    } & RACLinkProps;

    return (
      <a
        {...mergeProps(linkProps, hoverProps, focusProps, filteredProps)}
        data-focus-visible={isFocusVisible || undefined}
        data-focused={isFocused || undefined}
        data-hovered={isHovered || undefined}
        data-pressed={isPressed || undefined}
        ref={ref}
      />
    );
  },
);

RACLinkComponent.displayName = "RACLinkComponent";

const CreatedLinkComponent = createLink(RACLinkComponent);
